var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { css, jsx } from '@emotion/react';
import { useClickOutside, ZapierLogo, Icon, Link, Typography, } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useEffect, useState } from 'react';
import { skipLinkHref as skipLinkHrefDefault, UNIVERSAL_TOPBAR_CONDENSED_HEIGHT, UNIVERSAL_TOPBAR_HEIGHT, } from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';
import SkipLink from '../../SkipLink';
import UniversalTopbarLink from '../UniversalTopbarLink';
import AccountActions from '../AccountActions';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
const Styles = labelStyles('UniversalTopbar', {
    root: [
        {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
            position: 'relative',
            borderBottom: `1px solid var(--zds-gray-warm-3, #ECE9DF)`,
        },
    ],
    header: ({ hasSidebar }) => [
        css({
            width: '100%',
            height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
            padding: '0 22px 0 10px',
            display: 'grid',
            gridTemplateColumns: 'max-content auto max-content',
            alignItems: 'stretch',
            gap: '5px',
            backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
            '@media (min-width: 450px)': {
                height: UNIVERSAL_TOPBAR_HEIGHT,
                padding: '0 20px',
                gap: '0',
            },
            '@media (min-width: 1040px)': {
                padding: '0 40px',
            },
        }),
        hasSidebar &&
            withModifier('has-sidebar', css({
                maxWidth: 'none',
                paddingLeft: '10px',
            })),
    ],
    group: css({
        display: 'grid',
        gridAutoFlow: 'column',
        alignItems: 'center',
        justifySelf: 'flex-end',
        gap: '5px',
        '@media (min-width: 450px)': {
            gap: '17px',
        },
        'a[data-color=brand]': {
            height: '32px !important',
        },
    }),
    mainNavigationContent: [
        {
            display: 'flex',
            alignItems: 'stretch',
            marginLeft: 25,
        },
    ],
    integrationsCTA: css({
        display: 'flex',
        alignItems: 'center',
        borderRadius: `var(--zds-radius-small, 3px)`,
        height: 'var(--zds-size-36, 36px)',
        gap: '5px',
        color: 'var(--zds-gray-warm-9, #413735)',
        font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
        padding: '0 10px',
        '&:hover, &:focus, &:focus-within': {
            backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
        },
    }),
    integrationsCTAText: css({
        '@media (max-width: 1200px)': {
            display: 'none',
        },
    }),
    linkButton: css({
        '& span': {
            font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
            fontWeight: 'normal',
            color: 'var(--zds-gray-warm-10, #201515)',
        },
    }),
    navLinksList: (props) => [
        Object.assign({ display: 'grid' }, (props.links && {
            gridTemplateColumns: `repeat(${props.links.length + 1}, max-content)`,
        })),
    ],
    logoLinksWrapper: {
        position: 'relative',
    },
    skipLink: {
        position: 'absolute',
        top: '100%',
        whiteSpace: 'nowrap',
    },
});
const Links = (props) => {
    var _a;
    const [activeMenu, setActiveMenu] = useState('');
    const [activeElement, setActiveElement] = useState(null);
    const [setClickOutsideNav] = useClickOutside({
        onClickOutside: () => setActiveMenu(''),
    });
    const toggleMegaMenu = (menuId) => {
        if (activeMenu === menuId) {
            if (activeElement instanceof HTMLElement)
                activeElement.focus();
            setActiveMenu('');
        }
        else {
            if (activeElement instanceof HTMLElement)
                setActiveElement(document.activeElement);
            setActiveMenu(menuId);
        }
    };
    const closeMenu = () => setActiveMenu('');
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            if (activeElement instanceof HTMLElement)
                activeElement.focus();
            setActiveMenu('');
        }
    };
    useEffect(() => {
        document.addEventListener('keydown', handleEscape, { capture: false });
        return () => {
            document.removeEventListener('keydown', handleEscape, { capture: false });
        };
    });
    const links = (_a = props.links) === null || _a === void 0 ? void 0 : _a.map((link) => (jsx(UniversalTopbarLink, { ariaControls: link.text, isMenuOpen: activeMenu === link.text, key: link.text, navigationItem: link, onCloseMenu: closeMenu, position: "navigation", toggleMenu: () => toggleMegaMenu(link.text) }, link.text)));
    if (!links) {
        return null;
    }
    return (jsx("nav", { "aria-label": "Site Links", id: "site-nav", ref: setClickOutsideNav, "data-zds": true },
        jsx("ul", { css: Styles.navLinksList(props), "data-zds": true }, links)));
};
const UniversalTopbar = (_a) => {
    var _b, _c, _d, _e;
    var { links = undefined, logoAriaLabel = 'Go to the Zapier Homepage', logoHref = '/', onLogoClick = () => { }, selectedHref = '', shouldRenderLogo = true, size = 'medium', skipLinkHref = skipLinkHrefDefault, renderAccountMenu = (props) => (jsx(AccountActions, { selectedHref: props.selectedHref, shouldRenderSignIn: props.size !== 'small', shouldRenderSignUp: true, size: props.size })), renderNotifications = () => null } = _a, otherProps = __rest(_a, ["links", "logoAriaLabel", "logoHref", "onLogoClick", "selectedHref", "shouldRenderLogo", "size", "skipLinkHref", "renderAccountMenu", "renderNotifications"]);
    const { currentUrl, getFullyQualifiedURL, shouldRenderCallToAction, topNavData, } = useUniversalNavContext();
    const isOnAuthPage = currentUrl === '/app/login' || currentUrl === '/sign-up';
    const isOnOnboardingPage = currentUrl.includes('/app/onboarding');
    const props = Object.assign({ links,
        logoAriaLabel,
        logoHref,
        onLogoClick,
        renderAccountMenu,
        renderNotifications,
        selectedHref,
        shouldRenderLogo,
        size,
        skipLinkHref }, otherProps);
    const sidebar = !isOnAuthPage &&
        !isOnOnboardingPage &&
        props.renderSidebar &&
        props.renderSidebar(props);
    const logoSize = {
        width: 105,
        height: 28,
    };
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: (event) => {
            if (props.onClickOutside)
                props.onClickOutside(event);
        },
    });
    const callToActionUrl = getFullyQualifiedURL(`${(_b = topNavData.callToAction) === null || _b === void 0 ? void 0 : _b.href}?demo_source=cs_nav_header_${currentUrl}`) || '';
    return (jsx("div", { css: Styles.root, ref: (node) => setClickOutsideNode(node), "data-zds": true },
        jsx("div", { css: Styles.header({
                hasSidebar: !!sidebar,
            }), "data-testid": "universalTopbar", onClick: props.onClick, "data-zds": true },
            jsx("div", { css: Styles.group, "data-zds": true },
                sidebar,
                props.shouldRenderLogo && (jsx("header", { css: Styles.logoLinksWrapper, "data-zds": true },
                    jsx("a", { "aria-label": props.logoAriaLabel, href: getFullyQualifiedURL(props.logoHref), onClick: props.onLogoClick, "data-zds": true },
                        jsx(ZapierLogo, Object.assign({}, logoSize))),
                    jsx("div", { css: Styles.skipLink, "data-zds": true },
                        jsx(SkipLink, { href: props.skipLinkHref }))))),
            jsx("div", { css: Styles.mainNavigationContent, "data-zds": true }, !isOnAuthPage && links && links.length > 0 && (jsx(Links, Object.assign({}, props, { links: links })))),
            jsx("div", { "aria-label": "Manage your account and explore apps", css: Styles.group, role: "form", "data-zds": true },
                jsx(Link, { color: null, css: Styles.integrationsCTA, href: (_c = topNavData.integrationsCTA) === null || _c === void 0 ? void 0 : _c.href, onClick: () => {
                        var _a, _b;
                        emitUserInteractionEvent({
                            interaction_name: 'call_to_action',
                            interaction_raw_text: (_a = topNavData.integrationsCTA) === null || _a === void 0 ? void 0 : _a.text,
                            destination_href: (_b = topNavData.integrationsCTA) === null || _b === void 0 ? void 0 : _b.href,
                            event_action: 'click',
                            option_selected: 'topbar_integrations_cta',
                            experiment_flag: true,
                            experiment_name: 'content-platforms-explore-integrations-cta',
                        });
                    } },
                    topNavData.integrationsCTA && (jsx(Icon, { name: (_d = topNavData.integrationsCTA) === null || _d === void 0 ? void 0 : _d.icon, size: 20 })),
                    jsx("span", { css: Styles.integrationsCTAText, "data-zds": true }, (_e = topNavData.integrationsCTA) === null || _e === void 0 ? void 0 : _e.text)),
                topNavData.callToAction &&
                    props.size !== 'small' &&
                    shouldRenderCallToAction && (jsx(UniversalTopbarLink, { navigationItem: Object.assign(Object.assign({ variant: 'link' }, topNavData.callToAction), { href: callToActionUrl }), position: "actions-menu", isButton: false }, topNavData.callToAction.text)),
                props.renderAccountMenu(props)))));
};
export default UniversalTopbar;
//# sourceMappingURL=OutOfAppTopbar.js.map
// @zapier/universal-layout@9.12.4 (a02ac1bd0)
